/* eslint-disable brace-style */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { AbstractDialog, AbstractDialogResultData, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { TableColumnTypes, TableConfig, TableStickyTypes } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { CropAid, UnitOfMeasurementEnum } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import { FertilizerImportDialogComponent } from 'src/app/fertilizers/fertilizer-import-dialog/fertilizer-import-dialog.component';
// eslint-disable-next-line max-len
import { FertilizerImportTypeDialogComponent } from 'src/app/fertilizers/fertilizer-import-dialog/fertilizer-import-type-dialog/fertilizer-import-type-dialog.component';
import {
  FertilizerTableRow,
  GetAnimalManureTransportResponse,
  ManureResponse,
  VdmTransportResponse,
  VDMTransportStatus,
} from 'src/app/fertilizers/models/animal-manure-transport.model';
import {
  DeleteFertilizersBySourceRequest,
  FertilizerFromDelivery,
  FertilizerRequest,
  FertilizerSource,
  FertilizerType,
  VDMTableType,
} from 'src/app/fertilizers/models/fertilizer.model';
import * as FertilizersActions from 'src/app/fertilizers/store/actions';
import { ManureSpaceApiService } from 'src/app/manure-space/services/manure-space-api.service';
import { setFertilizersFromDelivery } from 'src/app/manure-space/store/manure-space.actions';
import * as ManureSpaceSelectors from 'src/app/manure-space/store/manure-space.selectors';
import { SalesOrderPeriod } from 'src/app/sales-order-period/models/sales-order-period.models';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { CustomManureAbstractDialogData } from 'src/app/shared/components/dialog/abstract-dialog';
import { AppState } from 'src/app/shared/models';
import { SoilType } from 'src/app/soil-type/models/soil-type.models';
import { selectSoilTypes } from 'src/app/soil-type/store/soil-type.selectors';

export interface AnimalManureTransportTableRequestData extends CustomManureAbstractDialogData {
  kvkNumber: string;
  startDate: string;
  endDate: string;
  defaultSoilType: number;
  animalManureTransports: GetAnimalManureTransportResponse;
}

@Component({
  selector: 'app-animal-manure-transports-table',
  templateUrl: './animal-manure-transports-table.component.html',
  styleUrls: ['./animal-manure-transports-table.component.scss'],
  standalone: false,
})
export class AnimalManureTransportsTableComponent
  extends AbstractDialog<AnimalManureTransportTableRequestData>
  implements OnInit, AfterViewInit, OnDestroy
{
  data = inject<AnimalManureTransportTableRequestData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        AnimalManureTransportsTableComponent,
        AbstractDialogResultData<AnimalManureTransportTableRequestData>
      >
    >(MatDialogRef);
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly manureSpaceAPIService = inject(ManureSpaceApiService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialog = inject(DialogService);
  matDialog = inject(MatDialog);

  receivedTransportsTableConfig: TableConfig;
  dispatchedTransportsTableConfig: TableConfig;
  animalManureTransports: GetAnimalManureTransportResponse;
  showDisabledImportMessage = false;

  @ViewChild('periodIdCell') periodIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('soilTypeIdCell') soilTypeIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('statusCell') statusTemplateRef: TemplateRef<unknown>;

  private readonly destroyed$ = new Subject<boolean>();

  private readonly salesOrderPeriods$ = this.store.pipe(
    select(SalesOrderPeriodSelectors.selectSalesOrderPeriods),
  );
  private salesOrderPeriods: SalesOrderPeriod[] = [];

  private cropAids$ = this.store.pipe(select(selectCropAids), takeUntil(this.destroyed$));
  private soilTypes$ = this.store.pipe(select(selectSoilTypes), takeUntil(this.destroyed$));
  private cropAids: CropAid[] = [];
  private soilTypes: SoilType[] = [];
  private hasViewInitialized = false;
  private hasLoadedTransportContext = false;
  private hasInitializedTransportTables = false;

  manureSpaceId: number;

  ngOnInit(): void {
    this.store
      .select(ManureSpaceSelectors.selectManureSpaceDetailsId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((manureSpaceId) => {
        this.manureSpaceId = manureSpaceId;
      });

    combineLatest([this.salesOrderPeriods$, this.cropAids$, this.soilTypes$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([sop, cropAids, soilTypes]) => {
          this.salesOrderPeriods = sop;
          this.cropAids = cropAids;
          this.soilTypes = soilTypes;
          this.hasLoadedTransportContext = true;
          this.tryInitializeTransportTables();
        },
      });
  }

  ngAfterViewInit(): void {
    this.hasViewInitialized = true;
    this.tryInitializeTransportTables();
  }

  confirmImportVdm(): void {
    const receivedFertilizersRequests: FertilizerRequest[] =
      this.receivedTransportsTableConfig.dataSource.data.map((row: FertilizerTableRow) =>
        this.mapRowToRequest(row, FertilizerType.Delivered),
      );

    const dispatchedFertilizersRequests: FertilizerRequest[] =
      this.dispatchedTransportsTableConfig.dataSource.data.map((row: FertilizerTableRow) =>
        this.mapRowToRequest(row, FertilizerType.Dispatched),
      );

    this.openOptionDialog([...receivedFertilizersRequests, ...dispatchedFertilizersRequests]);
  }

  getPeriodName = (periodId: number): string =>
    this.salesOrderPeriods.find((p) => p.id === periodId)?.name;

  getSoilTypeName = (soilTypeId: number): string =>
    this.soilTypes.find((s) => s.id === soilTypeId)?.name;

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  isRowInvalid = (row: FertilizerTableRow) =>
    row.fertilizerId === 0 || row.soilTypeId === 0 || row.periodId === 0;

  private setImportButtonState(): void {
    // Import button should be disabled if there are manures that don't have linked crop aids.
    // Import button should also be disabled if there are fertilizers with unit of measurement other than kg, g and ton
    if (
      this.receivedTransportsTableConfig.dataSource.data.some(
        (row: FertilizerTableRow) => row.fertilizerId === 0,
      ) ||
      this.dispatchedTransportsTableConfig.dataSource.data.some(
        (row: FertilizerTableRow) => row.fertilizerId === 0,
      )
    ) {
      this.data.disableConfirmButton = true;
      this.data.dialogSubmitWarningMessage = this.locale.translate(
        'sharedTransport.table.importDisabledMessage',
      );
    } else {
      this.data.disableConfirmButton = false;
      this.data.dialogSubmitWarningMessage = null;
    }

    if (this.hasNoTransportLines()) {
      this.data.disableConfirmButton = true;
      this.data.dialogSubmitWarningMessage = this.locale.translate(
        'sharedTransport.table.noLinesToImport',
      );
    } else if (this.hasCropAidsInvalidUnitOfMeasurement()) {
      this.data.disableConfirmButton = true;
      this.data.dialogSubmitWarningMessage = this.locale.translate(
        'sharedTransport.table.invalidUomToImport',
      );
    }
  }

  private matchDateToPeriodId(date: string): number {
    const period = this.salesOrderPeriods.find(
      (p) => p.periodNumber === new Date(date).getMonth() + 1,
    );

    return period != null ? period.id : 0;
  }

  private tryInitializeTransportTables(): void {
    if (
      !this.hasViewInitialized ||
      !this.hasLoadedTransportContext ||
      this.hasInitializedTransportTables
    ) {
      return;
    }

    this.initializeTransportTables(this.data.animalManureTransports);
    this.hasInitializedTransportTables = true;
    this.changeDetectorRef.detectChanges();
  }

  private initializeTransportTables(amt: GetAnimalManureTransportResponse): void {
    this.animalManureTransports = amt;

    let rowIndex = 0;

    const receivedTableData: FertilizerTableRow[] = [];
    for (const rvt of amt.receivedVdmTransports) {
      for (const manure of rvt.manures) {
        receivedTableData.push(
          this.mapResponseToTableRow(rvt, manure, VDMTableType.Received, rowIndex++),
        );
      }
    }

    rowIndex = 0;

    const dispatchedTableData: FertilizerTableRow[] = [];
    for (const rvt of amt.dispatchedVdmTransports) {
      for (const manure of rvt.manures) {
        dispatchedTableData.push(
          this.mapResponseToTableRow(rvt, manure, VDMTableType.Dispatched, rowIndex++),
        );
      }
    }

    this.receivedTransportsTableConfig = this.setTableConfig(
      receivedTableData,
      VDMTableType.Received,
    );
    this.dispatchedTransportsTableConfig = this.setTableConfig(
      dispatchedTableData,
      VDMTableType.Dispatched,
    );

    this.setImportButtonState();
  }

  private hasNoTransportLines(): boolean {
    return (
      !this.animalManureTransports ||
      (this.animalManureTransports.receivedVdmTransports.length === 0 &&
        this.animalManureTransports.dispatchedVdmTransports.length === 0)
    );
  }

  private hasCropAidsInvalidUnitOfMeasurement(): boolean {
    let hasInvalidUOM = false;

    const uniqueCropAidIdsFromManure = this.animalManureTransports.receivedVdmTransports
      .concat(this.animalManureTransports.dispatchedVdmTransports)
      .map((x) => x.manures)
      .reduce((p, n) => p.concat(n), [])
      .map((x) => x.linkedCropAidId)
      .filter((value, index, self) => self.indexOf(value) === index);

    uniqueCropAidIdsFromManure.forEach((cropAidId) => {
      const cropAid = this.cropAids.find((ca) => ca.id === cropAidId);
      if (
        cropAid &&
        cropAid.unitOfMeasurement !== UnitOfMeasurementEnum.G &&
        cropAid.unitOfMeasurement !== UnitOfMeasurementEnum.KG &&
        cropAid.unitOfMeasurement !== UnitOfMeasurementEnum.T
      ) {
        hasInvalidUOM = true;
      }
    });

    return hasInvalidUOM;
  }

  private setTableConfig(
    fertilizers: FertilizerTableRow[],
    vdmTableType: VDMTableType,
  ): TableConfig {
    return {
      dataSource: new MatTableDataSource<FertilizerTableRow>(fertilizers),
      activeSort: { id: 'loadDate', start: 'desc', disableClear: true },
      columns: [
        {
          id: 'status',
          type: TableColumnTypes.TEMPLATE,
          disableSort: true,
          template: this.statusTemplateRef,
        },
        {
          id: 'fertilizerName',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('sharedTransport.table.headers.fertilizer'),
          disableSort: true,
          className: 'flex__child--1',
          headerclassName: 'flex__child--1',
        },
        {
          id: 'comment',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('comments'),
          disableSort: true,
          className: 'flex__child--1',
          headerclassName: 'flex__child--1',
        },
        {
          id: 'periodId',
          type: TableColumnTypes.TEMPLATE,
          label: this.locale.translate('period'),
          disableSort: true,
          className: 'flex__child--1',
          headerClassName: 'flex__child--1',
          template: this.periodIdsTemplateRef,
        },
        {
          id: 'soilTypeId',
          type: TableColumnTypes.TEMPLATE,
          label: this.locale.translate('soilType'),
          disableSort: true,
          className: 'flex__child--1',
          headerClassName: 'flex__child--1',
          template: this.soilTypeIdsTemplateRef,
        },
        {
          id: 'quantity',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.amountInTon'),
          disableSort: true,
          className: 'flex__child--1',
          headerClassName: 'flex__child--1',
          digitsInfo: '1.1-1',
        },
        {
          id: 'nitrogen',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.nitrogenInKg'),
          disableSort: true,
          className: 'flex__child--1',
          headerClassName: 'flex__child--1',
          digitsInfo: '1.0-0',
        },
        {
          id: 'phosphate',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.phosphateInKg'),
          disableSort: true,
          className: 'flex__child--1',
          headerClassName: 'flex__child--1',
          digitsInfo: '1.0-0',
        },
      ],
      actionColumn: true,
      actions: [
        {
          id: 'edit',
          label: this.locale.translate('edit'),
          callback: (row: FertilizerTableRow) => {
            this.editFertilizerRow(row, vdmTableType);
          },
          icon: 'edit',
          disabled: () => false,
        },
        {
          id: 'delete',
          label: this.locale.translate('delete'),
          callback: (row: FertilizerTableRow) => {
            this.deleteFertilizerRow(row, vdmTableType);
          },
          icon: 'delete',
          disabled: () => false,
        },
      ],
      selectColumn: false,
      pagination: false,
      header: TableStickyTypes.STICKY,
      primaryColumn: ' ',
      alternateRows: true,
    } as TableConfig;
  }

  editFertilizerRow(row: FertilizerTableRow, vdmTableType: VDMTableType): void {
    this.store.dispatch(
      setFertilizersFromDelivery({
        fertilizers: [this.mapRowToFertilizerImportModel(row, vdmTableType)],
      }),
    );

    const dialogRef = this.matDialog.open(FertilizerImportDialogComponent, {
      id: 'fertilizer-import-dialog',
      data: {
        rowIndex: row.rowIndex,
        title: this.locale.translate('animalManureTransport.editVdmTransport'),
        confirmButtonText: this.locale.translate('save'),
        cancelButtonText: this.locale.translate('cancel'),
        cancelButton: false,
        confirmButton: false,
        isBatchEdit: true,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        const { result } = res;
        if (result) {
          if (vdmTableType === VDMTableType.Received) {
            this.mutateFertilizersTable(this.receivedTransportsTableConfig, result);
          } else if (vdmTableType === VDMTableType.Dispatched) {
            this.mutateFertilizersTable(this.dispatchedTransportsTableConfig, result);
          }
          this.setImportButtonState();
        }
      }
    });
  }

  deleteFertilizerRow(row: FertilizerTableRow, vdmTableType: VDMTableType) {
    if (vdmTableType === VDMTableType.Received) {
      this.receivedTransportsTableConfig.dataSource.data =
        this.receivedTransportsTableConfig.dataSource.data.filter(
          (r: FertilizerTableRow) => r.rowIndex !== row.rowIndex,
        );
    } else if (vdmTableType === VDMTableType.Dispatched) {
      this.dispatchedTransportsTableConfig.dataSource.data =
        this.dispatchedTransportsTableConfig.dataSource.data.filter(
          (r: FertilizerTableRow) => r.rowIndex !== row.rowIndex,
        );
    }

    this.setImportButtonState();
  }

  openOptionDialog(fertilizers: FertilizerRequest[]): void {
    this.dialog
      .responsiveDialog(FertilizerImportTypeDialogComponent, {
        text: this.locale.translate('animalManureTransport.importVDMQuestion'),
      })
      .subscribe(({ status, result }) => {
        if (status === 'CONFIRM') {
          if (result.result === 'overwrite') {
            this.deleteBeforeImport(fertilizers);
          } else if (result.result === 'add') {
            this.importWithoutDelete(fertilizers);
          }
        }
      });
  }

  importWithoutDelete(fertilizers: FertilizerRequest[]) {
    this.store.dispatch(FertilizersActions.importMineralBalance({ fertilizers }));
    this.confirm();
  }

  deleteBeforeImport(fertilizers: FertilizerRequest[]) {
    this.manureSpaceAPIService
      .deleteFertilizersBySource({
        manureSpaceId: this.manureSpaceId,
        fertilizerSource: FertilizerSource.ExternalVDM,
        fertilizerTypes: [
          FertilizerType.NotDelivered,
          FertilizerType.Delivered,
          FertilizerType.Dispatched,
        ],
      } as DeleteFertilizersBySourceRequest)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(FertilizersActions.importMineralBalance({ fertilizers }));
          this.confirm();
        },
        error: (_error) => {
          this.cancel();
          return this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: this.locale.translate(
                'animalManureTransport.table.GenericImportErrorMessage',
              ),
            }),
          );
        },
      });
  }

  private mapResponseToTableRow = (
    rvt: VdmTransportResponse,
    manure: ManureResponse,
    transportType: VDMTableType,
    rowIndex: number,
  ): FertilizerTableRow => ({
    rowIndex: rowIndex++,
    nitrogen: (manure.percentage / 100) * rvt.nitrogenInKg,
    phosphate: (manure.percentage / 100) * rvt.phosphateInKg,
    quantity: (manure.percentage / 100) * rvt.amountInTon,
    soilTypeId: this.data.defaultSoilType,
    comment: this.generateComment(rvt, transportType),
    periodId:
      rvt.status === VDMTransportStatus.InProgress
        ? this.matchDateToPeriodId(rvt.loadDate)
        : this.matchDateToPeriodId(rvt.unloadDate),
    fertilizerName:
      this.cropAids.find((ca) => ca.id === manure.linkedCropAidId)?.name ??
      this.locale.translate('unknownFertilizer'),
    fertilizerId: this.cropAids.find((ca) => ca.id === manure.linkedCropAidId)?.id ?? 0,
    manureCode: isNaN(Number(manure.code)) ? 0 : Number(manure.code),
  });

  private generateComment = (vdm: VdmTransportResponse, transportType: VDMTableType): string =>
    transportType === VDMTableType.Received
      ? `${this.locale.translate('sharedTransport.table.headers.unloadDate')} ${vdm.unloadDate}`
      : `${this.locale.translate('sharedTransport.table.headers.loadDate')} ${vdm.loadDate}`;

  private mapRowToFertilizerImportModel = (
    row: FertilizerTableRow,
    vdmTableType: VDMTableType,
  ): FertilizerFromDelivery => ({
    cropAidId: row.fertilizerId,
    dosage: row.quantity,
    comment: row.comment,
    totalNitrogen: row.nitrogen,
    totalPhosphate: row.phosphate,
    periodId: row.periodId,
    fertilizerType:
      vdmTableType === VDMTableType.Received ? FertilizerType.Delivered : FertilizerType.Dispatched,
    soilTypeId: row.soilTypeId,
    manureCode: row.manureCode,
  });

  private mapRowToRequest = (
    row: FertilizerTableRow,
    fertilizerType: FertilizerType,
  ): FertilizerRequest => ({
    cropAidId: row.fertilizerId,
    soilTypeId: row.soilTypeId,
    periodId: row.periodId,
    totalNitrogen: row.nitrogen,
    totalPhosphate: row.phosphate,
    dosage: row.quantity,
    comment: row.comment,
    fertilizerType,
    fertilizerSource: FertilizerSource.ExternalVDM,
    fertilizerId: null,
    manureCode: row.manureCode,
  });

  private mutateFertilizersTable(
    table: TableConfig,
    result: {
      comment: string;
      periodId: number;
      soilTypeId: number;
      dosage: number;
      cropAidId: number;
      totalNitrogen: number;
      totalPhosphate: number;
      rowIndex: number;
    },
  ) {
    const fertRow = table.dataSource.data.find(
      (row: FertilizerTableRow) => row.rowIndex === result.rowIndex,
    ) as FertilizerTableRow | undefined;
    if (!fertRow) {
      console.error('Failed to find fertRow', table.dataSource.data, result);
      return;
    }
    fertRow.comment = result.comment;
    fertRow.periodId = result.periodId;
    fertRow.soilTypeId = result.soilTypeId;
    fertRow.quantity = result.dosage;
    fertRow.fertilizerId = result.cropAidId;
    fertRow.nitrogen = result.totalNitrogen;
    fertRow.phosphate = result.totalPhosphate;
    fertRow.fertilizerName = this.cropAids.find((ca) => ca.id === result.cropAidId)?.name;
  }
}
